import { create } from "zustand";
import { getChatConversations, getChatMessages } from "../../../api/api";
import { useAuthStore } from "./useAuthStore";

const normalizeConversation = (conversation) => {
  const lastMessage = conversation.messages?.[0];
  return {
    conversation_id: conversation.id,
    recipient: conversation.participant,
    message: lastMessage?.content || "",
    latest_time: conversation.last_message_at || conversation.updated_at,
  };
};

const normalizeMessage = (message) => ({
  id: message.id || message.messageId,
  sender_id: message.sender_id || message.senderId,
  message: message.content || message.message || "",
  created_at: message.created_at || message.createdAt,
});

export const useChatStore = create((set, get) => ({
  messages: [],
  users: [],
  selectedUser: null,
  isUsersLoading: false,
  isMessagesLoading: false,
  error: null,
  socketHandlers: null,

  getUsers: async () => {
    set({ isUsersLoading: true, error: null });
    try {
      const response = await getChatConversations();
      const conversations = (response.data?.data || []).map(
        normalizeConversation
      );
      set({ users: conversations });
      return conversations;
    } catch (error) {
      set({ error: error?.response?.data?.message || "Unable to load chats" });
      return [];
    } finally {
      set({ isUsersLoading: false });
    }
  },

  getMessages: async (conversationId) => {
    if (!conversationId) return;
    set({ isMessagesLoading: true, error: null });
    try {
      const response = await getChatMessages(conversationId);
      set({ messages: (response.data?.messages || []).map(normalizeMessage) });
    } catch (error) {
      set({
        messages: [],
        error: error?.response?.data?.message || "Unable to load messages",
      });
    } finally {
      set({ isMessagesLoading: false });
    }
  },

  sendMessage: async (messageData) => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    const content = messageData?.content?.trim();
    if (!selectedUser?.recipient?.id || !content) return false;
    if (!socket?.connected) {
      set({ error: "Chat is not connected" });
      return false;
    }
    socket.emit("privateMessage", {
      recipientId: selectedUser.recipient.id,
      content,
    });
    set({ error: null });
    return true;
  },

  subscribeToMessages: () => {
    get().unsubscribeFromMessages();
    const { selectedUser } = get();
    if (!selectedUser) return;

    const socket = useAuthStore.getState().socket;
    if (!socket) return;

    const privateMessageHandler = (newMessage) => {
      const current = get().selectedUser;
      if (
        Number(newMessage.conversationId) !== Number(current?.conversation_id)
      ) {
        return;
      }
      set({ messages: [...get().messages, normalizeMessage(newMessage)] });
    };

    const messageSentHandler = (newMessage) => {
      const current = get().selectedUser;
      if (
        Number(newMessage.conversationId) !== Number(current?.conversation_id)
      ) {
        return;
      }
      set({ messages: [...get().messages, normalizeMessage(newMessage)] });
    };

    socket.on("privateMessage", privateMessageHandler);
    socket.on("messageSent", messageSentHandler);
    set({ socketHandlers: { privateMessageHandler, messageSentHandler } });
  },

  unsubscribeFromMessages: () => {
    const socket = useAuthStore.getState().socket;
    const handlers = get().socketHandlers;
    if (handlers) {
      socket?.off("privateMessage", handlers.privateMessageHandler);
      socket?.off("messageSent", handlers.messageSentHandler);
    }
    set({ socketHandlers: null });
  },

  upsertConversation: (conversation) => {
    set((state) => {
      const exists = state.users.some(
        (item) => item.conversation_id === conversation.conversation_id
      );
      return { users: exists ? state.users : [conversation, ...state.users] };
    });
  },

  setSelectedUser: (selectedUser) => set({ selectedUser, messages: [] }),
}));
